import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { useThemeStore } from '@/stores';
import { AuthProvider } from '@/providers/AuthProvider';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppLayout } from '@/layouts/AppLayout';
import { ToastContainer } from '@/components/ui';
import { PageLoadingSkeleton } from '@/components/PageLoadingSkeleton';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Eagerly loaded pages (entry points)
import { LandingPage } from '@/pages/Landing';
import { LoginPage } from '@/pages/Login';
import { SignupPage } from '@/pages/Signup';
import { AuthCallbackPage } from '@/pages/AuthCallback';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { ResetPasswordPage } from '@/pages/ResetPassword';

// Lazy loaded protected pages
const DashboardPage = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.DashboardPage })));
const AskAIPage = lazy(() => import('@/pages/AskAI').then(m => ({ default: m.AskAIPage })));
const ReaderPage = lazy(() => import('@/pages/Reader').then(m => ({ default: m.ReaderPage })));
const FlashcardsPage = lazy(() => import('@/pages/Flashcards').then(m => ({ default: m.FlashcardsPage })));
const QuizzesPage = lazy(() => import('@/pages/Quizzes').then(m => ({ default: m.QuizzesPage })));
const PracticeTestsPage = lazy(() => import('@/pages/PracticeTests').then(m => ({ default: m.PracticeTestsPage })));
const PodcastsPage = lazy(() => import('@/pages/Podcasts').then(m => ({ default: m.PodcastsPage })));
const LecturesPage = lazy(() => import('@/pages/Lectures').then(m => ({ default: m.LecturesPage })));
const ClassesPage = lazy(() => import('@/pages/Classes').then(m => ({ default: m.ClassesPage })));
const ProgressPage = lazy(() => import('@/pages/Progress').then(m => ({ default: m.ProgressPage })));
const PricingPage = lazy(() => import('@/pages/Pricing').then(m => ({ default: m.PricingPage })));
const SettingsPage = lazy(() => import('@/pages/Settings').then(m => ({ default: m.SettingsPage })));
const AdminPage = lazy(() => import('@/pages/Admin').then(m => ({ default: m.AdminPage })));
const OnboardingPage = lazy(() => import('@/pages/Onboarding').then(m => ({ default: m.OnboardingPage })));
const AdminRoute = lazy(() => import('@/components/AdminRoute').then(m => ({ default: m.AdminRoute })));

// Sentry test page — verify telemetry is working
const SentryTestPage = () => {
  const [results, setResults] = useState<string[]>([]);

  const addResult = (msg: string) => setResults((prev) => [...prev, msg]);

  const handleTestError = () => {
    Sentry.captureException(new Error('Test error from Koda!'));
    addResult('Error sent to Sentry');
  };

  const handleTestBreadcrumb = () => {
    Sentry.addBreadcrumb({ category: 'test', message: 'Test breadcrumb', level: 'info' });
    Sentry.captureException(new Error('Test error with breadcrumb'));
    addResult('Breadcrumb + error sent');
  };

  const handleTestSpan = () => {
    Sentry.startSpan({ name: 'test.span', op: 'test' }, () => {
      // Simulate work
      for (let i = 0; i < 1000000; i++) { /* noop */ }
    });
    addResult('Performance span sent');
  };

  const user = Sentry.getCurrentScope().getUser();
  const release = __SENTRY_RELEASE__;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8 max-w-md">
        <h1 className="text-2xl font-bold text-foreground mb-4">Sentry Telemetry Test</h1>

        <div className="text-left mb-6 p-4 bg-foreground/5 rounded-md text-sm font-mono">
          <p><span className="text-muted-foreground">Release:</span> {release}</p>
          <p><span className="text-muted-foreground">User:</span> {user ? `${user.email} (${user.id})` : 'Not set'}</p>
          <p><span className="text-muted-foreground">Environment:</span> {import.meta.env.MODE}</p>
        </div>

        <div className="flex flex-col gap-3 mb-6">
          <button onClick={handleTestError} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
            Test Error
          </button>
          <button onClick={handleTestBreadcrumb} className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600">
            Test Breadcrumb + Error
          </button>
          <button onClick={handleTestSpan} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
            Test Performance Span
          </button>
        </div>

        {results.length > 0 && (
          <div className="text-left p-3 bg-green-500/10 rounded-md">
            {results.map((r, i) => (
              <p key={i} className="text-green-600 text-sm">{r}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-background-primary">
    <div className="text-center p-8">
      <h1 className="font-serif text-6xl mb-4">404</h1>
      <p className="text-foreground-secondary text-lg mb-6">
        The page you're looking for doesn't exist.
      </p>
      <a
        href="/"
        className="inline-flex items-center gap-2 px-6 py-3 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors"
      >
        Go to Home
      </a>
    </div>
  </div>
);

function AppRoutes() {
  const { theme } = useThemeStore();

  // Apply theme class on mount and changes
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return (
    <SentryRoutes>
      {/* Landing page */}
      <Route path="/" element={<LandingPage />} />

      {/* Auth routes (no sidebar, no protection) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes without sidebar */}
      <Route element={<ProtectedRoute />}>
        <Route path="/onboarding" element={<Suspense fallback={<PageLoadingSkeleton />}><OnboardingPage /></Suspense>} />
        <Route path="/test" element={<SentryTestPage />} />
      </Route>

      {/* Protected app routes with sidebar layout (Suspense in AppLayout wraps Outlet) */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/ask" element={<AskAIPage />} />
          <Route path="/reader" element={<ReaderPage />} />
          <Route path="/reader/:documentId" element={<ReaderPage />} />
          <Route path="/flashcards" element={<FlashcardsPage />} />
          <Route path="/quizzes" element={<QuizzesPage />} />
          <Route path="/practice" element={<PracticeTestsPage />} />
          <Route path="/podcasts" element={<PodcastsPage />} />
          <Route path="/lectures" element={<LecturesPage />} />
          <Route path="/classes" element={<ClassesPage />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route element={<Suspense fallback={<PageLoadingSkeleton />}><AdminRoute /></Suspense>}>
            <Route path="/admin" element={<AdminPage />} />
          </Route>
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </SentryRoutes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ToastContainer />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
